'use client';

import { useEffect } from 'react';
import { AppShell } from '@mantine/core';
import { usePathname, useRouter } from 'next/navigation';

import { fetchBilling } from '@/app/settings/billing-api';
import { useSidebarState } from '@/hooks/useSidebarState';
import { useApiStatus } from '@/hooks/useApiStatus';
import { useAuthStore, useHasHydrated } from '@/stores/authStore';
import { useAppModeStore } from '@/stores/appModeStore';
import { readyLoopClient } from '@/lib/client';
import { Sidebar } from './Sidebar';
import { BottomTabs } from './BottomTabs';
import { ConnectivityOverlay } from './ConnectivityOverlay';
import { AuthGateModal } from '@/components/AuthGateModal';
import { usePendingIntentResume } from '@/hooks/usePendingIntentResume';

interface AppLayoutProps {
  children: React.ReactNode;
}

const PUBLIC_PAGES = ['/login', '/login/', '/auth/callback'];

/** Authenticated pages that render without the sidebar/app shell. */
const CHROMELESS_PAGES = ['/subscribe', '/subscribe/'];

export function AppLayout({ children }: AppLayoutProps) {
  const router = useRouter();
  const { collapsed, expand, collapse } = useSidebarState();
  const sessionToken = useAuthStore((s) => s.sessionToken);
  const aurigaUid = useAuthStore((s) => s.aurigaUid);
  const logout = useAuthStore((s) => s.logout);
  const hasHydrated = useHasHydrated();
  const pathname = usePathname();

  const hasSubscription = useAuthStore((s) => s.hasSubscription);
  const subscriptionChecked = useAuthStore((s) => s.subscriptionChecked);
  const planSelected = useAuthStore((s) => s.planSelected);

  const normalizedPath = pathname;
  const isPublicPage = PUBLIC_PAGES.some(
    (p) => normalizedPath === p || normalizedPath.startsWith('/auth/'),
  );
  const isChromelessPage = CHROMELESS_PAGES.some(
    (p) => normalizedPath === p,
  );

  // Keep ReadyLoopClient auth in sync with the auth store
  const userEmail = useAuthStore((s) => s.userEmail);
  useEffect(() => {
    if (sessionToken && aurigaUid) {
      readyLoopClient.setAuth({ sessionToken, aurigaUid, userEmail: userEmail ?? undefined });
    }
  }, [sessionToken, aurigaUid, userEmail]);

  // Auth redirect for unauthenticated users.
  // In SBAA / app mode we intentionally let unauthed users land in
  // the app shell; gated actions (chat submit, welcome card, voice
  // connect) pop the auth modal instead. Outside SBAA, keep the
  // hard redirect.
  const appLoading = useAppModeStore((s) => s.loading);
  const isAppModeForAuthGate = useAppModeStore((s) => s.isAppMode);
  useEffect(() => {
    if (hasHydrated && !isPublicPage && !appLoading) {
      if (!sessionToken || !aurigaUid) {
        if (!isAppModeForAuthGate) {
          logout();
          router.replace('/login');
        }
      }
    }
  }, [
    hasHydrated,
    sessionToken,
    aurigaUid,
    isPublicPage,
    appLoading,
    isAppModeForAuthGate,
    router,
    logout,
  ]);

  // Subscription check: fetch billing on first load for authenticated users
  useEffect(() => {
    if (!hasHydrated || isPublicPage || !sessionToken || !aurigaUid) return;
    if (subscriptionChecked) return;

    let cancelled = false;
    (async () => {
      try {
        const billing = await fetchBilling(aurigaUid, sessionToken);
        if (!cancelled) {
          useAuthStore.setState({
            hasSubscription: billing.has_subscription,
            planSelected: billing.plan_selected,
            subscriptionChecked: true,
          });
        }
      } catch {
        // On error, mark as checked so we don't block the app
        if (!cancelled) {
          useAuthStore.setState({ subscriptionChecked: true });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [hasHydrated, isPublicPage, sessionToken, aurigaUid, subscriptionChecked]);

  // Re-detect app mode after client-side navigation (e.g. OAuth
  // callback -> app route).  The module-level _initialDetect in
  // appModeStore may be stale if the module loaded at /auth/callback.
  useEffect(() => {
    useAppModeStore.getState().initialize();
  }, [pathname]);

  const isAppMode = useAppModeStore((s) => s.isAppMode);

  // Subscription gate: redirect to /subscribe if user hasn't subscribed or dismissed.
  // In app mode, skip — billing is accessible in Settings but shouldn't block entry.
  useEffect(() => {
    if (!hasHydrated || isPublicPage || isChromelessPage) return;
    if (!sessionToken || !aurigaUid) return;
    if (!subscriptionChecked) return;

    if (!planSelected && !isAppMode) {
      router.replace('/subscribe');
    }
  }, [hasHydrated, isPublicPage, isChromelessPage, sessionToken, aurigaUid, subscriptionChecked, planSelected, isAppMode, router]);

  // App mode route guard: redirect non-app pages to /chat. Unauthed
  // SBAA users also can't reach /settings (Settings is hidden for
  // them), so redirect those to /chat too.
  useEffect(() => {
    if (!isAppMode || !hasHydrated || isPublicPage || isChromelessPage || appLoading) return;
    const isAuthed = Boolean(sessionToken && aurigaUid);
    const appAllowedPaths = isAuthed
      ? ['/chat', '/voice', '/settings']
      : ['/chat', '/voice'];
    const isAllowed = appAllowedPaths.some(
      (p) => normalizedPath === p || normalizedPath === p + '/' || normalizedPath.startsWith(p + '/'),
    );
    if (!isAllowed) {
      router.replace('/chat');
    }
  }, [isAppMode, hasHydrated, isPublicPage, isChromelessPage, appLoading, sessionToken, aurigaUid, normalizedPath, router]);

  // Replay any PendingIntent once the user signs in.
  usePendingIntentResume();

  const { status: apiStatus } = useApiStatus();

  if (!hasHydrated || isPublicPage) {
    return <div style={{ flex: 1, minHeight: 0 }}>{children}</div>;
  }

  const isAuthed = Boolean(sessionToken && aurigaUid);

  // Outside SBAA, unauthed users are redirected to /login — render
  // nothing while that happens.
  if (!isAuthed && !isAppMode) {
    return <div style={{ flex: 1, minHeight: 0 }} />;
  }

  // Chromeless pages: auth required but no sidebar
  if (isChromelessPage) {
    return <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>{children}</div>;
  }

  return (
    <AppShell
      navbar={{
        width: collapsed ? 56 : 220,
        breakpoint: 'sm',
        collapsed: { mobile: true },
      }}
      padding={0}
      transitionDuration={200}
      transitionTimingFunction="ease-out"
      style={{ flex: 1, minHeight: 0 }}
    >
      <AppShell.Navbar
        onMouseEnter={expand}
        onMouseLeave={collapse}
        style={{ borderRight: '1px solid var(--rl-border)' }}
      >
        <Sidebar
          collapsed={collapsed}
          onMouseEnter={expand}
          onMouseLeave={collapse}
        />
      </AppShell.Navbar>

      <AppShell.Main
        style={{
          height: '100%',
          background: 'var(--rl-bg)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          {children}
        </div>
        <BottomTabs />
      </AppShell.Main>
      <ConnectivityOverlay status={apiStatus} />
      <AuthGateModal />
    </AppShell>
  );
}
